<script setup lang="ts">
import { Deferred, Head, Link, router } from '@inertiajs/vue3'
import { ArrowLeft, CheckCircle2, Clock, ExternalLink, Eye, FileUp, Send } from 'lucide-vue-next'
import { computed, reactive, ref } from 'vue'

import DocumentUploadCard from '@/components/applications/DocumentUploadCard.vue'
import StatusBadge from '@/components/applications/StatusBadge.vue'
import StatusTimeline from '@/components/applications/StatusTimeline.vue'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import PortalLayout from '@/layouts/PortalLayout.vue'
import { formatAmount, formatLabel } from '@/lib/utils'
import { portalCustomerApplicationDocumentsPath, portalCustomerApplicationsPath } from '@/routes'

interface Application {
  id: string
  status: string
  loan_type: string
  purpose: string
  amount: number
  loan_term: number | null
  submission_channel: string | null
  taken_at: string | null
  accepted_at: string | null
  approved_at: string | null
  settled_at: string | null
  rejected_at: string | null
  rejection_reason: string | null
  rejected_on: string | null
  created_at: string
}

interface AppDocument {
  id: number
  name: string
  status: string
  rejection_reason: string | null
  file_name: string | null
  file_url: string | null
}

const props = defineProps<{
  application: Application
  documents?: AppDocument[] | null
  has_bank_statement_request: boolean
  bank_statement_url: string | null
}>()

function formatType(t: string) { return t.charAt(0).toUpperCase() + t.slice(1) }

const uploadable = (doc: AppDocument) => doc.status === 'requested' || doc.status === 'rejected'

const uploadableDocs = computed(() => (props.documents ?? []).filter(uploadable))
const nonUploadableDocs = computed(() => (props.documents ?? []).filter((d) => !uploadable(d)))

const selectedFiles = reactive<Record<number, File | null>>({})
const fileErrors = reactive<Record<number, string | null>>({})
const processing = ref(false)

const previewDoc = ref<AppDocument | null>(null)

function onFileSelect(docId: number, file: File) {
  selectedFiles[docId] = file
  fileErrors[docId] = null
}

function onFileError(docId: number, message: string) {
  selectedFiles[docId] = null
  fileErrors[docId] = message
}

function onFileClear(docId: number) {
  selectedFiles[docId] = null
  fileErrors[docId] = null
}

const selectedCount = computed(() => uploadableDocs.value.filter((doc) => selectedFiles[doc.id]).length)

const hasAllFiles = computed(() =>
  uploadableDocs.value.length > 0 && uploadableDocs.value.every((doc) => selectedFiles[doc.id]),
)

const hasAnyError = computed(() =>
  Object.values(fileErrors).some((e) => e !== null && e !== undefined),
)

function submitAll() {
  if (!hasAllFiles.value || hasAnyError.value || processing.value) return
  processing.value = true

  const uploads = uploadableDocs.value
    .filter((doc) => selectedFiles[doc.id])
    .map((doc) => ({ name: doc.name, file: selectedFiles[doc.id] as File }))

  router.post(
    portalCustomerApplicationDocumentsPath(props.application.id),
    { uploads },
    {
      preserveScroll: true,
      onFinish: () => {
        processing.value = false
        ;(props.documents ?? []).forEach((doc) => { selectedFiles[doc.id] = null })
      },
    },
  )
}
</script>

<template>
  <Head title="Track Application" />
  <PortalLayout actor-type="customer">
    <div class="flex flex-col gap-6">

      <!-- Page heading -->
      <div class="flex items-center gap-3">
        <Link :href="portalCustomerApplicationsPath()" class="text-muted-foreground hover:text-foreground">
          <ArrowLeft class="size-4" />
        </Link>
        <h1 class="text-xl font-semibold">{{ formatType(application.loan_type) }} Application</h1>
        <StatusBadge :status="application.status" />
      </div>

      <!-- Progress timeline -->
      <Card class="order-2 md:order-1">
        <CardHeader><CardTitle class="text-base">Progress</CardTitle></CardHeader>
        <CardContent>
          <StatusTimeline
            :status="application.status"
            :created-at="application.created_at"
            :taken-at="application.taken_at"
            :accepted-at="application.accepted_at"
            :approved-at="application.approved_at"
            :settled-at="application.settled_at"
            :rejected-at="application.rejected_at"
            :rejection-reason="application.rejection_reason"
            :rejected-on="application.rejected_on"
          />
        </CardContent>
      </Card>

      <!-- Details -->
      <Card class="order-1 md:order-2">
        <CardHeader><CardTitle class="text-base">Details</CardTitle></CardHeader>
        <CardContent>
          <dl class="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
            <div>
              <dt class="text-muted-foreground">Type</dt>
              <dd class="mt-1 font-medium">{{ formatType(application.loan_type) }}</dd>
            </div>
            <div>
              <dt class="text-muted-foreground">Amount</dt>
              <dd class="mt-1 font-medium">{{ formatAmount(application.amount) }}</dd>
            </div>
            <div v-if="application.loan_term">
              <dt class="text-muted-foreground">Term</dt>
              <dd class="mt-1 font-medium">{{ application.loan_term }} months</dd>
            </div>
            <div>
              <dt class="text-muted-foreground">Purpose</dt>
              <dd class="mt-1 font-medium">{{ formatLabel(application.purpose) }}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      <!-- Documents (hidden for rejected/settled) -->
      <Deferred
        v-if="application.status !== 'rejected' && application.status !== 'settled'"
        data="documents"
      >
        <template #fallback>
          <Card class="order-3">
            <CardContent class="space-y-3 py-4">
              <div class="h-12 animate-pulse rounded-lg bg-muted" />
              <div class="h-12 animate-pulse rounded-lg bg-muted" />
            </CardContent>
          </Card>
        </template>

        <Card v-if="(documents && documents.length > 0) || has_bank_statement_request" class="order-3">
          <CardHeader>
            <CardTitle class="text-base">Documents</CardTitle>
          </CardHeader>
          <CardContent class="space-y-4">

            <!-- ── Upload-needed section ───────────────────────────────────── -->
            <div v-if="uploadableDocs.length > 0" class="space-y-2">

              <!-- Progress bar -->
              <div class="mb-3 rounded-lg border border-border bg-muted/30 p-3">
                <div class="mb-1.5 flex items-center justify-between text-xs font-medium">
                  <span class="text-muted-foreground">Upload progress</span>
                  <span :class="hasAllFiles ? 'text-emerald-500 dark:text-emerald-400' : 'text-amber-500 dark:text-amber-400'">
                    {{ selectedCount }}/{{ uploadableDocs.length }} ready
                  </span>
                </div>
                <div class="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    class="h-full rounded-full transition-all duration-500"
                    :class="hasAllFiles ? 'bg-emerald-500' : 'bg-amber-400'"
                    :style="{ width: uploadableDocs.length ? `${(selectedCount / uploadableDocs.length) * 100}%` : '0%' }"
                  />
                </div>
              </div>

              <!-- Upload cards -->
              <DocumentUploadCard
                v-for="doc in uploadableDocs"
                :key="doc.id"
                :doc="doc"
                :selected-file="selectedFiles[doc.id] ?? null"
                :error="fileErrors[doc.id] ?? null"
                @select="(file) => onFileSelect(doc.id, file)"
                @error="(message) => onFileError(doc.id, message)"
                @clear="onFileClear(doc.id)"
              />

              <!-- Submit button -->
              <Button
                class="w-full gap-2 font-semibold"
                :disabled="!hasAllFiles || hasAnyError || processing"
                @click="submitAll"
              >
                <FileUp class="size-4" />
                <span v-if="processing">Uploading…</span>
                <span v-else-if="hasAllFiles">Upload {{ uploadableDocs.length }} document{{ uploadableDocs.length !== 1 ? 's' : '' }}</span>
                <span v-else>Select all documents to continue ({{ selectedCount }}/{{ uploadableDocs.length }})</span>
              </Button>
            </div>

            <!-- Bank statement: requested via Illion, not uploaded here, so it never
                 appears in the list above (Document.for_customer filters it out). -->
            <div
              v-if="has_bank_statement_request && bank_statement_url"
              class="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-center text-sm text-blue-800 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-300"
            >
              Please also upload your bank statements securely at
              <a
                :href="bank_statement_url"
                target="_blank"
                class="font-medium underline hover:opacity-80"
              >bankstatements.com.au</a>
              and follow the prompts. For assistance call <strong>1300 045 047</strong>.
            </div>

            <!-- ── Submitted docs (uploaded / accepted / sent / generated) ── -->
            <div v-if="nonUploadableDocs.length > 0" class="space-y-1.5">
              <p v-if="uploadableDocs.length > 0" class="pb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Submitted
              </p>
              <div
                v-for="doc in nonUploadableDocs"
                :key="doc.id"
                class="flex items-center gap-3 rounded-lg border border-border bg-muted/20 px-3 py-2.5"
              >
                <!-- Status icon -->
                <div
                  class="flex size-7 shrink-0 items-center justify-center rounded-full"
                  :class="{
                    'bg-emerald-100 dark:bg-emerald-900/50': doc.status === 'accepted',
                    'bg-blue-100 dark:bg-blue-900/40': doc.status === 'uploaded',
                    'bg-muted': doc.status === 'sent' || doc.status === 'generated',
                  }"
                >
                  <CheckCircle2 v-if="doc.status === 'accepted'" class="size-4 text-emerald-600 dark:text-emerald-400" />
                  <Clock v-else-if="doc.status === 'uploaded'" class="size-4 text-blue-500 dark:text-blue-400" />
                  <Send v-else class="size-4 text-muted-foreground" />
                </div>

                <!-- Name & file -->
                <div class="min-w-0 flex-1">
                  <p class="truncate text-sm font-medium">{{ doc.name }}</p>
                  <p v-if="doc.file_name && doc.status === 'uploaded'" class="truncate text-xs text-muted-foreground">
                    {{ doc.file_name }}
                  </p>
                </div>

                <!-- Status pill -->
                <span
                  class="shrink-0 rounded-full px-2 py-0.5 text-xs font-medium"
                  :class="{
                    'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400': doc.status === 'accepted',
                    'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400': doc.status === 'uploaded',
                    'bg-muted text-muted-foreground': doc.status === 'sent' || doc.status === 'generated',
                  }"
                >
                  <span v-if="doc.status === 'accepted'">Accepted</span>
                  <span v-else-if="doc.status === 'uploaded'">Under review</span>
                  <span v-else>Sent</span>
                </span>

                <!-- View button (sent docs only) -->
                <button
                  v-if="doc.file_url"
                  type="button"
                  class="text-muted-foreground hover:text-foreground shrink-0 rounded p-1 transition-colors"
                  title="View document"
                  @click="previewDoc = doc"
                >
                  <Eye class="size-4" />
                </button>
              </div>
            </div>

          </CardContent>
        </Card>
      </Deferred>

      <!-- Document preview dialog -->
      <Dialog :open="previewDoc !== null" @update:open="(v) => { if (!v) previewDoc = null }">
        <DialogContent class="flex max-h-[90vh] flex-col sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle class="truncate pr-6">{{ previewDoc?.name }}</DialogTitle>
          </DialogHeader>

          <div class="min-h-0 flex-1 overflow-hidden rounded-md border bg-gray-100">
            <iframe
              v-if="previewDoc?.file_url"
              :src="previewDoc.file_url"
              class="h-full min-h-[60vh] w-full"
              frameborder="0"
            />
          </div>

          <div class="pt-1">
            <a
              v-if="previewDoc?.file_url"
              :href="previewDoc.file_url"
              target="_blank"
              class="text-primary inline-flex items-center gap-1 text-xs hover:underline"
            >
              <ExternalLink class="size-3" />
              Open in new tab
            </a>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  </PortalLayout>
</template>
